import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import NumberFlow from "@number-flow/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ListOrdered,
  PlusIcon,
  Trash2Icon,
  Hand,
  Clock,
  MessageSquare,
  FolderInput,
  BellRing,
  CircleDotIcon,
  CircleIcon,
  InfoIcon,
  MoreVerticalIcon,
  CheckIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
  SwitchCardDescription,
} from "@/components/ui/switch-card";
import { Slider } from "@/components/ui/slider";
import { AnimatePresence, motion } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCampaignStore } from "@/stores";
import { Stage } from "@/types/types";
import { Frame, FrameHeader, FrameTitle, FramePanel, FrameFooter } from "@/components/ui/frame";
import EmptyState from "@/components/ui/empty-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { isEveCampaign } from "@/lib/eve";
import PipelineTab from "./PipelineTab";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { cn } from "@/lib/utils";

const DEFAULT_STAGE: Omit<Stage, "id" | "order"> = {
  name: "",
  trigger: "time_delay",
  delay_seconds: 86400,
  template: "",
  follow_up_enabled: false,
  follow_up_wait_time: 172800,
  max_follow_ups: 2,
  requires_reply: false,
  collect_field: "",
  color: "bg-purple-500/10 text-purple-500",
  description: "Stage - replaces status after activation",
  trigger_field: null,
  trigger_field_before: false,
};

const COLOR_OPTIONS = [
  {
    label: "Purple",
    value: "bg-purple-500/10 text-purple-500",
    dot: "bg-purple-500",
  },
  { label: "Blue", value: "bg-blue-500/10 text-blue-500", dot: "bg-blue-500" },
  { label: "Cyan", value: "bg-cyan-500/10 text-cyan-500", dot: "bg-cyan-500" },
  {
    label: "Emerald",
    value: "bg-emerald-500/10 text-emerald-500",
    dot: "bg-emerald-500",
  },
  {
    label: "Amber",
    value: "bg-amber-500/10 text-amber-500",
    dot: "bg-amber-500",
  },
  {
    label: "Orange",
    value: "bg-orange-500/10 text-orange-500",
    dot: "bg-orange-500",
  },
  { label: "Pink", value: "bg-pink-500/10 text-pink-500", dot: "bg-pink-500" },
  { label: "Rose", value: "bg-rose-500/10 text-rose-500", dot: "bg-rose-500" },
  {
    label: "Violet",
    value: "bg-violet-500/10 text-violet-500",
    dot: "bg-violet-500",
  },
  {
    label: "Indigo",
    value: "bg-indigo-500/10 text-indigo-500",
    dot: "bg-indigo-500",
  },
];

const TRIGGER_OPTIONS: {
  value: Stage["trigger"];
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
    {
      value: "manual",
      label: "Manual",
      description:
        "Wait for you to advance the creator manually in the dashboard with the button",
      icon: Hand,
    },
    {
      value: "time_delay",
      label: "Time Delay",
      description:
        "Wait a set number of days before sending the email automatically",
      icon: Clock,
    },
  ];

export default function StagesTab() {
  const { campaign } = useCampaignStore();
  // Eve campaigns never run legacy stages — the same route shows the
  // read-only Amy pipeline instead (tab label switches to "Pipeline").
  // Separate component so the legacy editor's hooks never mount for eve.
  if (isEveCampaign(campaign)) return <PipelineTab />;
  return <LegacyStagesTab />;
}

function LegacyStagesTab() {
  const { getToken } = useAuth();
  const { campaign, campaignId, updateStages } = useCampaignStore();

  const [stages, setStages] = useState<Stage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [collectFieldInput, setCollectFieldInput] = useState("");
  const hasChanges =
    JSON.stringify(stages) !==
    JSON.stringify(
      [...(campaign?.stages || [])].sort((a, b) => a.order - b.order),
    );

  useEffect(() => {
    if (campaign?.stages) {
      const sorted = [...campaign.stages].sort((a, b) => a.order - b.order);
      setStages(sorted);
      if (!selectedId && sorted.length > 0) {
        setSelectedId(sorted[0].id);
      }
    }
  }, [campaign?.stages]);

  const selectedStage = stages.find((s) => s.id === selectedId);

  useEffect(() => {
    setCollectFieldInput(selectedStage?.collect_field || "");
  }, [selectedId]);
  const isStageComplete = (s: Stage) => !!(s.name && s.color && s.template);
  const allValid = stages.length === 0 || stages.every(isStageComplete);

  function updateStage(id: string, updates: Partial<Stage>) {
    setStages((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    );
  }

  function addStage() {
    const newStage: Stage = {
      ...DEFAULT_STAGE,
      id: crypto.randomUUID(),
      order: stages.length,
    };
    setStages((prev) => [...prev, newStage]);
    setSelectedId(newStage.id);
  }

  function removeStage(id: string) {
    setStages((prev) =>
      prev.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })),
    );
    if (selectedId === id) setSelectedId(null);
  }

  function moveStage(index: number, direction: "up" | "down") {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= stages.length) return;

    setStages((prev) => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated.map((s, i) => ({ ...s, order: i }));
    });
  }

  function handleCollectFieldChange(id: string, value: string) {
    setCollectFieldInput(value);
    const slug = value
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    updateStage(id, { collect_field: slug || null });
  }

  async function handleSave() {
    const token = await getToken();
    if (!token || !campaignId) return;
    await updateStages(campaignId, stages, token);
  }

  return (
      <div className="flex flex-col max-w-6xl mx-auto h-auto p-2 pb-0">
        {stages.length > 0 && (
          <div className="hidden md:flex items-center gap-1.5 md:px-5 py-3 flex-wrap">
            <BadgeStatus
              status="activated"
              size="sm"
              showDescription={false}
              showIcon={false}
            />
            {stages.map((stage) => (
              <span key={stage.id} className="flex items-center gap-1.5">
                <ChevronRightIcon className="size-3 text-muted-foreground/40" />
                <button
                  type="button"
                  onClick={() => setSelectedId(stage.id)}
                  className={cn(
                    "transition-all",
                    selectedId === stage.id
                      ? "scale-105"
                      : "opacity-60 hover:opacity-90",
                  )}
                >
                  <BadgeStatus
                    status={stage.id}
                    stages={stages}
                    size="sm"
                    showDescription={false}
                    showIcon={false}
                  />
                </button>
              </span>
            ))}
            <ChevronRightIcon className="size-3 text-muted-foreground/40" />
            <BadgeStatus
              status="completed"
              size="sm"
              showDescription={false}
              showIcon={false}
            />
          </div>
        )}

        <div className="flex md:flex-row flex-col h-auto">
          {/* ── SIDEBAR ── */}
          <div className="flex md:flex-col flex-row gap-1 md:max-w-72 md:min-w-72 w-full h-min sticky md:top-0 top-0 rounded-lg p-3 overflow-auto bg-background z-10">
            <div className="hidden md:flex items-center justify-between w-full pb-1 border-b mb-2">
              <h2 className="text-base font-medium flex w-full items-center gap-2">
                <div className="flex items-center gap-2">
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger className="flex flex-row items-center gap-2 rounded-md hover:bg-muted cursor-help px-1.5 h-7">
                      Stages
                      <InfoIcon className="size-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      After a creator is activated, their status is replaced by
                      the first stage. They progress through each stage in order
                      until all stages are completed.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Button
                  variant="muted-ghost"
                  size="icon-sm"
                  className="ml-auto"
                  title="Add Stage"
                  onClick={addStage}
                >
                  <PlusIcon className="size-3.5" />
                </Button>
              </h2>
            </div>

            {stages.map((stage, index) => {
              const isActive = selectedId === stage.id;
              return (
                <ScopeWrapper
                  key={stage.id}
                  data-context-menu
                  onClick={() => setSelectedId(stage.id)}
                  className={`
                  flex items-center gap-1 group w-full pr-0.5 pl-2 h-8 rounded-md transition-none cursor-pointer
                  ${isActive ? "bg-muted font-medium" : "hover:bg-sidebar"}`}
                >
                  <span className="text-xs font-mono shrink-0 mx-1 text-muted-foreground">
                    {index + 1}.
                  </span>
                  <span className="truncate text-xs">
                    {stage.name || "Untitled Stage"}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="muted-ghost"
                        size="icon-sm"
                        scope={false}
                        className="rounded-sm shrink-0 ml-auto opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                      >
                        <MoreVerticalIcon className="size-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="w-40"
                    >
                      <DropdownMenuItem
                        onClick={() => moveStage(index, "up")}
                        disabled={index === 0}
                      >
                        <ChevronUpIcon />
                        Move Up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => moveStage(index, "down")}
                        disabled={index === stages.length - 1}
                      >
                        <ChevronDownIcon />
                        Move Down
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => removeStage(stage.id)}
                        variant="destructive"
                      >
                        <Trash2Icon />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ScopeWrapper>
              );
            })}

            {stages.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4 hidden md:block">
                No stages yet
              </p>
            )}

            {/* Mobile add button */}
            <Button
              onClick={addStage}
              variant="ghost"
              size="sm"
              className="md:hidden flex shrink-0"
            >
              <PlusIcon className="size-4" />
              Add
            </Button>
          </div>

          {/* ── EDITOR ── */}
          <div className="w-full py-4 pb-0 space-y-8 h-auto pl-1">
            {/* Empty state — no stages */}
            {stages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16">
                <EmptyState icon={<ListOrdered />} text="No stages configured" />
                <span className="mt-6 mb-3 max-w-sm text-balance text-center text-xs text-muted-foreground">
                  Add stages to define what happens after a creator is
                  activated. Each stage replaces the creator's status and can
                  send emails, collect data, or wait for manual approval.
                </span>
                <Button onClick={addStage} size="sm">
                  Add Your First Stage
                </Button>
              </div>
            )}

            {/* Stage editor form */}
            {selectedStage && (
              <div
                key={selectedStage.id}
                className="animate-in fade-in-0 slide-in-from-top-2 duration-200 space-y-10 pb-40"
              >
                {/* ── Stage Name & Color ── */}
                <Frame>
                  <FrameHeader><FrameTitle>Stage Name & Color</FrameTitle></FrameHeader>
                  <FramePanel>
                    <Input
                      placeholder="e.g. Delivery Check"
                      autoFocus={selectedStage.name === ""}
                      value={selectedStage.name}
                      onChange={(e) =>
                        updateStage(selectedStage.id, { name: e.target.value })
                      }
                      className="lg:w-2/5 w-full"
                    />
                  </FramePanel>
                  <FramePanel>
                    <div className="flex flex-wrap gap-2 rounded-md p-1 -m-1">
                      {COLOR_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            updateStage(selectedStage.id, { color: opt.value })
                          }
                          className={cn(
                            "size-7 rounded-md transition-all flex items-center justify-center",
                            opt.dot,
                            selectedStage.color === opt.value
                              ? "rotate-45"
                              : "hover:ring-4 ring-muted-foreground/15 cursor-pointer hover:scale-95",
                          )}
                          title={opt.label}
                        >
                          {selectedStage.color === opt.value && (
                            <CheckIcon
                              className="size-3.5 text-white -rotate-45"
                              strokeWidth={3}
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </FramePanel>
                </Frame>

                {/* ── Trigger ── */}
                <Frame>
                  <FrameHeader><FrameTitle>Trigger</FrameTitle></FrameHeader>
                  <FramePanel className={`${selectedStage.trigger === "time_delay" ? "p-4" : "p-0 bg-transparent border-none shadow-none"} transition-all`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {TRIGGER_OPTIONS.map((opt) => {
                        const isSelected = selectedStage.trigger === opt.value;
                        return (
                          <Button
                            key={opt.value}
                            type="button"
                            variant="outline"
                            className={`relative hover:bg-sidebar flex flex-col items-start gap-2 rounded-lg border p-4 shadow-xs h-auto text-foreground hover:text-foreground whitespace-normal w-full ${isSelected
                              ? "border-ring ring-2 ring-primary/10 bg-background"
                              : ""
                              }`}
                            onClick={() =>
                              updateStage(selectedStage.id, {
                                trigger: opt.value,
                                delay_seconds:
                                  opt.value === "time_delay"
                                    ? selectedStage.delay_seconds || 86400
                                    : null,
                              })
                            }
                          >
                            <div className="flex items-center gap-2 w-full">
                              <opt.icon className="size-4 shrink-0 opacity-60" />
                              <span className="font-medium text-sm">
                                {opt.label}
                              </span>
                            </div>
                            <p className="text-muted-foreground text-xs text-left font-normal">
                              {opt.description}
                            </p>
                            {isSelected ? (
                              <CircleDotIcon className="absolute top-4 right-4 text-primary size-4 shrink-0 ring-2 rounded-full ring-primary/10" />
                            ) : (
                              <CircleIcon className="absolute top-4 right-4 text-input size-4 shrink-0" />
                            )}
                          </Button>
                        );
                      })}
                    </div>

                    {selectedStage.trigger === "time_delay" &&
                      (() => {
                        const dateFields = (campaign?.fields || []).filter(
                          (f) => f.type === "date",
                        );
                        const hasTriggerField = !!selectedStage.trigger_field;

                        return (
                          <div className="flex flex-col gap-4 md:max-w-2/3 max-w-11/12 mt-4 ml-3 pl-3 pb-4 border-l border-dashed animate-in fade-in-0 slide-in-from-top-2 duration-200">
                            {/* Delay slider */}
                            <div className="flex flex-col gap-4">
                              <div className="flex items-center justify-between gap-2">
                                <Label className="flex items-center gap-1">
                                  Days
                                  {hasTriggerField && (
                                    <Select
                                      value={
                                        selectedStage.trigger_field_before
                                          ? "before"
                                          : "after"
                                      }
                                      onValueChange={(val) =>
                                        updateStage(selectedStage.id, {
                                          trigger_field_before: val === "before",
                                        })
                                      }
                                    >
                                      <SelectTrigger className="w-fit h-7 px-1.5 rounded-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem
                                          className="h-7"
                                          value="before"
                                        >
                                          before
                                        </SelectItem>
                                        <SelectItem className="h-7" value="after">
                                          after
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {!hasTriggerField && <span>after</span>}
                                  the
                                  {dateFields.length > 0 && (
                                    <Select
                                      value={
                                        selectedStage.trigger_field || "__none__"
                                      }
                                      onValueChange={(val) =>
                                        updateStage(selectedStage.id, {
                                          trigger_field:
                                            val === "__none__" ? null : val,
                                          ...(val === "__none__"
                                            ? {
                                              trigger_field_before: false,
                                              delay_seconds:
                                                selectedStage.delay_seconds ||
                                                86400,
                                            }
                                            : {}),
                                        })
                                      }
                                    >
                                      <SelectTrigger className="w-fit h-7 px-1.5 rounded-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem
                                          className="h-7"
                                          value="__none__"
                                        >
                                          From activation (default)
                                        </SelectItem>
                                        {dateFields.map((f) => (
                                          <SelectItem
                                            key={f.name}
                                            value={f.name!}
                                            className="h-7"
                                          >
                                            {f.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {dateFields.length === 0 && (
                                    <span>
                                      activation/previous stage
                                      <span className="text-xs text-muted-foreground ml-1">
                                        (default)
                                      </span>
                                    </span>
                                  )}
                                </Label>
                                <span className="font-mono text-sm font-medium">
                                  <NumberFlow
                                    value={Math.round(
                                      (selectedStage.delay_seconds || 86400) /
                                      86400,
                                    )}
                                  />
                                </span>
                              </div>
                              <Slider
                                value={[
                                  Math.round(
                                    (selectedStage.delay_seconds ||
                                      (hasTriggerField ? 0 : 86400)) / 86400,
                                  ),
                                ]}
                                min={hasTriggerField ? 0 : 1}
                                max={30}
                                step={1}
                                onValueChange={([val]) =>
                                  updateStage(selectedStage.id, {
                                    delay_seconds: val * 86400,
                                  })
                                }
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Optional. Instead of counting delay from when the
                              creator enters this stage, base it on a date value
                              the creator provided from the CRM.
                            </span>
                          </div>
                        );
                      })()}
                  </FramePanel>
                </Frame>

                {/* ── Email Template Guideline ── */}
                <Frame>
                  <FrameHeader><FrameTitle>Email Template Guideline</FrameTitle></FrameHeader>
                  <Textarea
                    placeholder="Write the guideline for Amy. She will adapt it naturally for each creator..."
                    value={selectedStage.template}
                    onChange={(e) =>
                      updateStage(selectedStage.id, {
                        template: e.target.value,
                      })
                    }
                    rows={6}
                    variant="panel"
                    className="min-h-32"
                  />
                  <FrameFooter>
                    <p className="text-xs text-muted-foreground">
                      Amy uses this as a guideline - she won't copy it verbatim.
                    </p>
                  </FrameFooter>
                </Frame>

                {/* ── Completion ── */}
                <Frame>
                  <FrameHeader><FrameTitle>Completion</FrameTitle></FrameHeader>
                  <FramePanel className={`${selectedStage.requires_reply ? "p-4" : "p-0 border-none"} transition-all`}>
                    <SwitchCard>
                      <SwitchCardSwitch
                        checked={selectedStage.requires_reply}
                        onCheckedChange={(val) =>
                          updateStage(selectedStage.id, {
                            requires_reply: val,
                            collect_field: val
                              ? selectedStage.collect_field
                              : null,
                          })
                        }
                      />
                      <SwitchCardContent>
                        <SwitchCardLabel>
                          <MessageSquare className="size-4" />
                          Requires Reply
                          <span className="text-muted-foreground text-xs">
                            {" "}
                            - Creator must respond
                          </span>
                        </SwitchCardLabel>
                        <SwitchCardDescription className="pl-6">
                          Wait for the creator to respond before completing this
                          stage. When disabled, it completes after sending.
                        </SwitchCardDescription>
                      </SwitchCardContent>
                    </SwitchCard>

                    {selectedStage.requires_reply && (
                      <div className="space-y-1.5 mt-4 ml-3 pl-3 pb-4 border-l border-dashed animate-in fade-in-0 slide-in-from-top-2 duration-150">
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                            <Label className="flex items-center gap-2">
                              <FolderInput className="size-4 text-muted-foreground" />
                              Collect Field
                            </Label>
                            <InfoIcon className="size-3.5 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64">
                            Optional. Amy will extract this value from the
                            creator's reply and save it.
                          </TooltipContent>
                        </Tooltip>
                        <Input
                          placeholder="e.g. Shipping Address, Live Link"
                          autoFocus={selectedStage.collect_field === null}
                          value={collectFieldInput}
                          onChange={(e) =>
                            handleCollectFieldChange(
                              selectedStage.id,
                              e.target.value,
                            )
                          }
                          className="max-w-sm"
                        />
                        {collectFieldInput &&
                          collectFieldInput !==
                          (selectedStage.collect_field || "") && (
                            <p className="text-xs font-mono text-muted-foreground mt-1 ml-1 animate-in fade-in-0 slide-in-from-top-1 duration-150">
                              → {selectedStage.collect_field}
                            </p>
                          )}
                        {/* <p className="text-xs text-muted-foreground">
                        Optional. Amy will extract this value from the creator's
                        reply.
                      </p> */}
                      </div>
                    )}
                  </FramePanel>
                </Frame>

                {/* ── Follow-ups ── */}
                <Frame>
                  <FrameHeader><FrameTitle>Follow-ups</FrameTitle></FrameHeader>
                  <FramePanel className={`${selectedStage.follow_up_enabled ? "p-4" : "p-0 border-none"} transition-all`}>
                    <SwitchCard>
                      <SwitchCardSwitch
                        checked={selectedStage.follow_up_enabled}
                        onCheckedChange={(val) =>
                          updateStage(selectedStage.id, {
                            follow_up_enabled: val,
                          })
                        }
                      />
                      <SwitchCardContent>
                        <SwitchCardLabel>
                          <BellRing className="size-4" />
                          Follow-ups Enabled
                          <span className="text-muted-foreground text-xs">
                            {" "}
                            - Auto-remind unresponsive creators
                          </span>
                        </SwitchCardLabel>
                        <SwitchCardDescription className="pl-6">
                          Amy follows up if the creator doesn't respond within the
                          wait time.
                        </SwitchCardDescription>
                      </SwitchCardContent>
                    </SwitchCard>

                    {selectedStage.follow_up_enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 ml-3 pl-3 pb-4 border-l border-dashed animate-in fade-in-0 slide-in-from-top-2 duration-150">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <Tooltip delayDuration={500}>
                              <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                                <Label>Wait Time (days)</Label>
                                <InfoIcon className="size-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Days to wait before sending a follow-up
                              </TooltipContent>
                            </Tooltip>
                            <span className="font-mono text-sm font-medium">
                              <NumberFlow
                                value={Math.round(
                                  selectedStage.follow_up_wait_time / 86400,
                                )}
                              />
                            </span>
                          </div>
                          <Slider
                            value={[
                              Math.round(
                                selectedStage.follow_up_wait_time / 86400,
                              ),
                            ]}
                            min={1}
                            max={14}
                            step={1}
                            onValueChange={([val]) =>
                              updateStage(selectedStage.id, {
                                follow_up_wait_time: val * 86400,
                              })
                            }
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <Tooltip delayDuration={500}>
                              <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                                <Label>Max Follow-ups</Label>
                                <InfoIcon className="size-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Maximum number of follow-ups before Amy stops
                              </TooltipContent>
                            </Tooltip>
                            <span className="font-mono text-sm font-medium">
                              <NumberFlow value={selectedStage.max_follow_ups} />
                            </span>
                          </div>
                          <Slider
                            value={[selectedStage.max_follow_ups]}
                            min={0}
                            max={5}
                            step={1}
                            onValueChange={([val]) =>
                              updateStage(selectedStage.id, {
                                max_follow_ups: val,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </FramePanel>
                </Frame>
              </div>
            )}

            {/* Changes Buttons */}
            <AnimatePresence>
              {hasChanges && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="flex items-center w-full sticky bottom-0 pb-12 bg-linear-to-t from-background to-background/10"
                >
                  <motion.div
                    animate={
                      showErrors && !allValid
                        ? { x: [0, -8, 8, -8, 8, -8, 0] }
                        : {}
                    }
                    transition={{ duration: 0.5 }}
                    className="flex items-center w-md shadow-xl mx-auto gap-1.5 p-1.5 pl-4 border border-muted-foreground rounded-pill sticky bottom-8 bg-foreground"
                  >
                    <Label className="text-background/90 text-xs italic mr-auto">
                      <InfoIcon className="size-3.25 inline-block" />
                      {showErrors && !allValid
                        ? stages
                          .filter((s) => !isStageComplete(s))
                          .map((s) => {
                            const missing = [
                              !s.name && "name",
                              !s.color && "color",
                              !s.template && "template",
                            ].filter(Boolean);
                            return `${s.name || "Untitled"}: ${missing.join(", ")}`;
                          })
                          .join(" · ")
                        : "Unsaved changes"}
                    </Label>
                    <Button
                      className="bg-muted-foreground/40 hover:bg-muted-foreground/50 text-background/80 hover:text-background border-none h-7 px-2.5 rounded-pill"
                      variant="outline"
                      scope={false}
                      size="sm"
                      onClick={() => {
                        setStages(
                          [...(campaign?.stages || [])].sort(
                            (a, b) => a.order - b.order,
                          ),
                        );
                        setShowErrors(false);
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!allValid) {
                          setShowErrors(true);
                          return;
                        }
                        handleSave();
                      }}
                      className="h-7 px-2.5 rounded-pill"
                      scope={false}
                    >
                      Save Changes
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
  );
}
